import type { CatalogSection, CatalogCategorySelection } from '@/lib/category-filter';

interface CategoryNavProps {
  sections: CatalogSection[];
  selectedId: CatalogCategorySelection;
  onChange: (id: CatalogCategorySelection) => void;
}

export function CategoryNav({ sections, selectedId, onChange }: CategoryNavProps) {
  if (sections.length === 0) return null;
  const activeParent = sections.find(
    (section) =>
      section.id === selectedId || section.children.some((child) => child.id === selectedId),
  );

  return (
    <nav aria-label="Categorías de productos" className="mt-3 space-y-2">
      <div className="flex gap-2 overflow-x-auto pb-1">
        <CategoryButton
          label="Todos"
          pressed={selectedId === 'all'}
          onClick={() => onChange('all')}
        />
        {sections.map((section) => (
          <CategoryButton
            key={section.id}
            label={section.name}
            pressed={section.id === selectedId}
            onClick={() => onChange(section.id)}
          />
        ))}
      </div>
      {activeParent && activeParent.children.length > 0 && (
        <div
          className="flex gap-2 overflow-x-auto pb-1 pl-2"
          aria-label={`Subcategorías de ${activeParent.name}`}
        >
          {activeParent.children.map((child) => (
            <CategoryButton
              key={child.id}
              label={child.name}
              pressed={child.id === selectedId}
              secondary
              onClick={() => onChange(child.id)}
            />
          ))}
        </div>
      )}
    </nav>
  );
}

function CategoryButton({
  label,
  pressed,
  secondary = false,
  onClick,
}: {
  label: string;
  pressed: boolean;
  secondary?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`min-h-10 shrink-0 rounded-full border px-4 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
        pressed
          ? 'border-teal-700 bg-teal-700 text-white'
          : secondary
            ? 'border-neutral-200 bg-neutral-50 text-neutral-700 hover:border-teal-300'
            : 'border-neutral-300 bg-white text-neutral-700 hover:border-teal-400 hover:text-teal-800'
      }`}
    >
      {label}
    </button>
  );
}
